import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { API_BASE } from '../config';
import { hasEditAccess } from '../permissionConfig';
import { notifyEditBlocked } from '../components/EditAccessToast';
import { useSwrStore, useSwrQuery } from '../swrCache';
import { toDate, num, exportSheet, todayStamp } from '../excel';
import DataFreshness from '../components/DataFreshness';
import { ListHero, CountCard, ListTopRow, PdfButton, todayISO } from '../components/ListHero';
import { formatDate as fmtDate } from '../utils/formatDate';

// Sales Orders — the merged replacement for the old "SO Queue" and "Cancel SO"
// pages, which were near-duplicates (identical acc-so-list?view=advance query,
// identical columns) differing only in which button they showed. Unlike those,
// this page can reach EVERY SO ever made, not just the not-yet-invoiced /
// not-yet-packed slice they were both limited to.
//
// An SO's real state lives across four independent columns, so the backend
// collapses it to ONE status via a precedence chain (invoiced > kam_pending >
// warehouse_pending > packed). Those four buckets are mutually exclusive and
// exhaustive, so the filters partition the list — see acc-so-list.js. Cancelled
// SOs are hard-deleted into so_headers_del, so that filter reads a different
// table and its rows are strictly read-only.

const STATUSES = [
  { key: 'all',               label: 'All',                  fg: '#4a4150', bg: '#ece8f3', ring: 'rgba(104, 90, 160, 0.22)' },
  { key: 'invoiced',          label: 'Invoiced',             fg: '#1d6f7a', bg: '#d9eef1', ring: 'rgba(40, 130, 145, 0.22)' },
  { key: 'packed',            label: 'Packed',               fg: '#8a5a12', bg: '#f6e9d3', ring: 'rgba(170, 120, 40, 0.22)' },
  { key: 'warehouse_pending', label: 'Pending at Warehouse', fg: '#5b43c0', bg: '#e6e0f6', ring: 'rgba(110, 90, 170, 0.22)' },
  { key: 'kam_pending',       label: 'KAM Review Pending',   fg: '#9a5f0b', bg: '#f8ebcf', ring: 'rgba(180, 130, 30, 0.22)' },
  { key: 'cancelled',         label: 'Cancelled',            fg: '#a33b33', bg: '#f7dedb', ring: 'rgba(180, 70, 60, 0.22)' },
];
const SMAP = Object.fromEntries(STATUSES.map((s) => [s.key, s]));
const BOOKED_PILL = { label: 'Advance Booked', fg: '#2f7a52', bg: '#dcefe2', ring: 'rgba(60, 130, 90, 0.22)' };

const NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = [CURRENT_YEAR - 2, CURRENT_YEAR - 1, CURRENT_YEAR];

const clay  = '12px 14px 30px rgba(104, 90, 160, 0.18), -10px -10px 24px rgba(255,255,255,0.95)';
const inset = 'inset 5px 5px 12px rgba(104, 90, 160, 0.2), inset -5px -5px 12px rgba(255,255,255,0.95)';
const raised = '6px 7px 14px rgba(104, 90, 160, 0.16), -5px -5px 12px rgba(255,255,255,0.95)';

// Table column widths and the type/padding scale below are the mockup's
// values reduced ~20% — the list was reading too large inside the portal
// layout. Header and filter panel above are left at their original scale.
const GRID = '104px minmax(152px, 1.5fr) minmax(120px, 1.1fr) 88px 72px 56px 240px';
const TABLE_MIN_WIDTH = 965;

// Mirrors acc-so-list.js's precedence chain so the row's pill matches the
// bucket it was filtered into. Kept in lockstep with STATUS_FILTERS there.
function derivedStatus(so, cancelled) {
  if (cancelled) return 'cancelled';
  if (String(so.invoice_status || 'pending') === 'invoiced') return 'invoiced';
  if (!so.kam_validated) return 'kam_pending';
  if (String(so.validation_status || '') !== 'validated') return 'warehouse_pending';
  return 'packed';
}

function pillStyle(key, small) {
  const s = SMAP[key] || BOOKED_PILL;
  return {
    padding: small ? '7px 12px' : '9px 16px',
    borderRadius: 999,
    fontSize: small ? 11 : 12,
    fontWeight: 800,
    whiteSpace: 'nowrap',
    color: s.fg,
    background: s.bg,
    boxShadow: `inset 2px 2px 5px ${s.ring}, inset -2px -2px 5px rgba(255,255,255,0.9)`,
  };
}

function bookedPillStyle() {
  return {
    padding: '7px 12px', borderRadius: 999, fontSize: 11, fontWeight: 800, whiteSpace: 'nowrap',
    color: BOOKED_PILL.fg, background: BOOKED_PILL.bg,
    boxShadow: `inset 2px 2px 5px ${BOOKED_PILL.ring}, inset -2px -2px 5px rgba(255,255,255,0.9)`,
  };
}

export default function SalesOrders({ user }) {
  const [status, setStatus]       = useState('all');
  const [query, setQuery]         = useState('');
  // Defaults to today, same as Gifting & Dispatch.
  const [from, setFrom]           = useState(todayISO());
  const [to, setTo]               = useState(todayISO());
  const [month, setMonth]         = useState('all');
  const [year, setYear]           = useState('all');
  const [page, setPage]           = useState(1);

  const [openRow, setOpenRow]     = useState(null);   // voucher_no
  const [openedFromCancelled, setOpenedFromCancelled] = useState(false);
  const [detail, setDetail]       = useState(null);    // { header, line_items, cancelled }
  const [detailLoading, setDetailLoading] = useState(false);
  const [edited, setEdited]       = useState({});      // line id -> actual_qty
  const [saving, setSaving]       = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelAck, setCancelAck] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [toast, setToast]         = useState('');
  const [pdfLoading, setPdfLoading] = useState(false);
  const [exporting, setExporting]   = useState(false);
  const [exportProgress, setExportProgress] = useState('');
  const [rowPdfLoading, setRowPdfLoading] = useState(null); // voucher_no whose PDF is being fetched

  const displayName = useMemo(
    () => user?.display_name || user?.username || localStorage.getItem('display_name') || 'Accounts',
    [user]
  );

  const flash = (msg) => { setToast(msg); setTimeout(() => setToast(''), 2200); };

  // `query` drives the input (so typing stays instant); `debouncedQuery` is
  // what actually hits the server. Without this, every keystroke fired two
  // requests (list + counts = 4 SQL queries), which is what made the page feel
  // like it froze while typing.
  const [debouncedQuery, setDebouncedQuery] = useState('');
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedQuery(query);
      setPage(1); // batched with the line above -> one re-render, one fetch
    }, 350);
    return () => clearTimeout(t);
  }, [query]);

  const qs = useCallback(() => {
    const p = new URLSearchParams({ search: debouncedQuery, status });
    if (from) p.set('date_from', from);
    if (to)   p.set('date_to', to);
    return p;
  }, [debouncedQuery, status, from, to]);

  // Two caches so the eviction bounds can differ: list/counts entries hold 50
  // rows each and get cycled through fast (9 pages x 6 filters), while detail
  // entries are single SOs the user keeps reopening.
  const listStore   = useSwrStore(30);
  const detailStore = useSwrStore(30);

  // Detail opens are click-driven rather than key-driven, so they don't go
  // through useSwrQuery — they use the store directly (same shape as the
  // existing detailCache in Invoices.jsx) and need their own ordering guard.
  const detailReq = useRef(0);

  // The cache key IS the query string, and the fetcher builds its URL from that
  // same string — so a filter can never be part of the request but missing from
  // the key (which would serve one filter's rows under another's key).
  const listKey = useMemo(() => {
    const p = qs();
    p.set('view', 'list');
    p.set('page', String(page));
    return p.toString();
  }, [qs, page]);

  const list = useSwrQuery(listStore, listKey, useCallback(async () => {
    const res  = await fetch(`${API_BASE}/acc-so-list?${listKey}`);
    const data = await res.json();
    if (!res.ok || !data.success) throw new Error(data.message || `Server returned ${res.status}`);
    return data;
  }, [listKey]));

  const rows       = list.data?.so_list    || [];
  const totalPages = list.data?.total_pages || 1;
  const total      = list.data?.total       || 0;
  const loading    = list.loading;
  const error      = list.error;

  // Pill counts come from the server: with only 50 rows in hand the client
  // can't count the rest. The key deliberately omits `status` — the counts are
  // status-independent by definition, so including it meant every pill click
  // refired the query and got back identical numbers, wasting two SQL queries
  // per click. It only re-runs when the search or date range changes scope.
  const countsKey = useMemo(() => {
    const p = new URLSearchParams({ search: debouncedQuery, view: 'status_counts' });
    if (from) p.set('date_from', from);
    if (to)   p.set('date_to', to);
    return p.toString();
  }, [debouncedQuery, from, to]);

  const countsQuery = useSwrQuery(listStore, countsKey, useCallback(async () => {
    const res  = await fetch(`${API_BASE}/acc-so-list?${countsKey}`);
    const data = await res.json();
    if (!res.ok || !data.success) throw new Error(data.message || 'counts failed');
    return data;
  }, [countsKey]));

  // Counts are decorative — a failure leaves the pills unnumbered rather than
  // blocking the list, same as before.
  const counts = countsQuery.data?.counts || {};

  // Every mutation clears BOTH caches wholesale rather than evicting the
  // affected key: cancelling an SO changes the row set and the counts of
  // several filters at once, so a surviving entry for any other filter would
  // still be listing a cancelled order. Kept separate from `revalidate` below
  // because mutations must clear before re-reading the detail, or the modal
  // would repopulate from the pre-mutation cache entry.
  const clearCaches = useCallback(() => {
    listStore.clear();
    detailStore.clear();
  }, [listStore, detailStore]);

  const revalidate = useCallback(
    () => Promise.all([list.refresh(), countsQuery.refresh()]),
    [list, countsQuery]
  );

  // Any filter change invalidates the current page, so page resets to 1 — but
  // deliberately NOT via an effect keyed on the filters. Doing it that way
  // caused a double fetch on every filter change: `qs` changed and fired a
  // request, then the page reset fired a second one (and the first was for the
  // wrong page anyway). Instead each handler sets its filter and the page
  // together, which React batches into a single re-render and a single fetch.
  const changeFilters = (fn) => { fn(); setPage(1); };

  // Deep-link from a notification click (?q=<voucher_no>).
  useEffect(() => {
    const q = new URLSearchParams(window.location.search).get('q');
    // Drop the default today-only range, or an older SO would never match.
    if (q) { setQuery(q); setFrom(''); setTo(''); }
  }, []);

  // Month/Year SET the From/To range rather than acting as a second filter
  // ANDed with it — otherwise picking a month outside the current range
  // silently returns zero rows. Same approach as POTracker.jsx.
  function applyMonthYear(m, y) {
    if (m === 'all' && y === 'all') { setFrom(''); setTo(''); return; }
    const yr = y === 'all' ? CURRENT_YEAR : Number(y);
    if (m !== 'all') {
      const mo = Number(m);
      setFrom(`${yr}-${String(mo).padStart(2, '0')}-01`);
      setTo(new Date(yr, mo, 0).toISOString().slice(0, 10)); // day 0 of next month = last day of this
    } else {
      setFrom(`${yr}-01-01`);
      setTo(`${yr}-12-31`);
    }
  }

  const clearTime = () => changeFilters(() => { setFrom(''); setTo(''); setMonth('all'); setYear('all'); });

  // `fromCancelled` forces the backend to read the shadow table. Needed
  // because a voucher_no can exist in BOTH so_headers and so_headers_del (an
  // SO that was cancelled, then had its email re-ingested as a new live SO).
  // Without it, opening a row from the Cancelled filter silently showed the
  // unrelated live SO instead, with no cancellation details.
  async function openDetail(voucherNo, fromCancelled) {
    const src = fromCancelled ? '&source=cancelled' : '';
    const url = `view=detail&voucher_no=${encodeURIComponent(voucherNo)}${src}`;
    setOpenRow(voucherNo);
    setOpenedFromCancelled(!!fromCancelled);
    setEdited({});

    // `source=cancelled` is part of the key, not just the URL: a voucher_no can
    // exist in BOTH so_headers and so_headers_del (cancelled, then re-ingested
    // from email as a new live SO), so the live and cancelled reads of the same
    // voucher are genuinely different records and must not share a cache entry.
    const hit = detailStore.get(url);
    if (hit) {
      setDetail(hit.data);
      setDetailLoading(false);
      if (hit.fresh) return;
    } else {
      setDetail(null);
      setDetailLoading(true);
    }

    const token = ++detailReq.current;
    try {
      const res  = await fetch(`${API_BASE}/acc-so-list?${url}`);
      const data = await res.json();
      if (token !== detailReq.current) return; // user already opened another SO
      if (res.ok && data.success) {
        detailStore.set(url, data);
        setDetail(data);
      } else if (!hit) {
        setDetail({ error: data.message || 'Could not load this SO' });
      }
    } catch (err) {
      // Keep the cached copy on a failed background refresh rather than
      // replacing a readable modal with an error.
      if (token === detailReq.current && !hit) setDetail({ error: err.message });
    } finally {
      if (token === detailReq.current) setDetailLoading(false);
    }
    // No record-visits 'mark' call here: this page has no sidebar badge, so
    // marking rows visited would be a POST per row-open with nothing consuming
    // the result.
  }

  const closeDetail = () => { setOpenRow(null); setOpenedFromCancelled(false); setDetail(null); setEdited({}); setCancelOpen(false); };

  const header    = detail?.header;
  const isCancelled = !!detail?.cancelled;
  const isInvoiced  = header ? String(header.invoice_status || 'pending') === 'invoiced' : false;
  const isBooked    = header ? header.booking_type === 'advance_booked' : false;
  // Per the mockup: advance booking is offered unless the SO is invoiced or
  // cancelled. A packed SO is deliberately still eligible.
  const canBook     = !!header && !isCancelled && !isInvoiced;

  async function toggleBooking() {
    if (!hasEditAccess(user, 'invoicing', 'so_queue')) return notifyEditBlocked();
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/acc-advance-booking`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          voucher_no: openRow,
          booking_type: isBooked ? 'normal' : 'advance_booked',
          updated_by: displayName,
        }),
      });
      const text = await res.text();
      if (!res.ok) throw new Error(text || 'Failed to update booking');
      flash(isBooked ? 'Advance booking removed' : 'Marked as advance booked');
      clearCaches();
      await openDetail(openRow, openedFromCancelled);
      await revalidate();
    } catch (err) {
      flash(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function saveQuantities() {
    if (!hasEditAccess(user, 'invoicing', 'so_queue')) return notifyEditBlocked();
    const line_items = Object.entries(edited)
      .filter(([, v]) => v !== '' && v !== null && v !== undefined)
      .map(([id, v]) => ({ id: Number(id), actual_qty: Number(v) }));
    if (line_items.length === 0) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/acc-advance-booking`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          voucher_no: openRow,
          booking_type: header.booking_type || 'normal',
          updated_by: displayName,
          line_items,
        }),
      });
      if (!res.ok) throw new Error(await res.text() || 'Failed to save quantities');
      flash('Quantities saved');
      clearCaches();
      await openDetail(openRow, openedFromCancelled);
      await revalidate();
    } catch (err) {
      flash(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function confirmCancel() {
    if (!hasEditAccess(user, 'invoicing', 'cancel_so')) return notifyEditBlocked();
    setCancelling(true);
    try {
      const res = await fetch(`${API_BASE}/acc-cancel?action=cancel_so`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ voucher_no: openRow, reason: cancelReason.trim(), cancelled_by: displayName }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed to cancel SO');
      setCancelOpen(false);
      setCancelReason('');
      setCancelAck(false);
      closeDetail();
      flash(`SO ${openRow} cancelled`);
      clearCaches();
      await revalidate();
    } catch (err) {
      flash(err.message);
    } finally {
      setCancelling(false);
    }
  }

  async function viewPdf() {
    setPdfLoading(true);
    try {
      const res  = await fetch(`${API_BASE}/acc-so-list?view=so_pdf&voucher_no=${encodeURIComponent(openRow)}${openedFromCancelled ? '&source=cancelled' : ''}`);
      const data = await res.json();
      if (res.ok && data.success && data.pdf_url) window.open(data.pdf_url, '_blank', 'noopener');
      else flash('No PDF available for this SO.');
    } catch {
      flash('Failed to load SO PDF.');
    } finally {
      setPdfLoading(false);
    }
  }

  // PDF column — opens the SO PDF without opening the detail modal. The tab is
  // opened synchronously (inside the click) and pointed at the URL once it
  // arrives; opening it after the await would get caught by popup blockers.
  async function openRowPdf(e, voucherNo) {
    e.stopPropagation();
    if (rowPdfLoading) return;
    const tab = window.open('', '_blank');
    setRowPdfLoading(voucherNo);
    try {
      const src = status === 'cancelled' ? '&source=cancelled' : '';
      const res  = await fetch(`${API_BASE}/acc-so-list?view=so_pdf&voucher_no=${encodeURIComponent(voucherNo)}${src}`);
      const data = await res.json();
      if (res.ok && data.success && data.pdf_url) {
        if (tab) { tab.opener = null; tab.location.href = data.pdf_url; }
        else window.open(data.pdf_url, '_blank', 'noopener');
      } else {
        if (tab) tab.close();
        flash('No PDF available for this SO.');
      }
    } catch {
      if (tab) tab.close();
      flash('Failed to load SO PDF.');
    } finally {
      setRowPdfLoading(null);
    }
  }

  // Exports every row matching the CURRENT filters, not just the 50 on screen —
  // a partial export is too easy to mistake for the whole set. acc-so-list.js
  // hardcodes pageSize 50 with no page_size parameter, so the only way to get
  // the full set without a backend change is to walk the pages.
  //
  // Deliberately fetches fresh instead of reading the SWR cache: the file should
  // be one consistent snapshot, not a mix of cached and live pages.
  async function downloadExcel() {
    if (exporting) return;
    setExporting(true);
    setExportProgress('');
    try {
      const cancelled = status === 'cancelled';
      const all = [];
      let pages = 1;

      for (let p = 1; p <= pages; p++) {
        const qp = qs();
        qp.set('view', 'list');
        qp.set('page', String(p));
        const res  = await fetch(`${API_BASE}/acc-so-list?${qp.toString()}`);
        const data = await res.json();
        if (!res.ok || !data.success) throw new Error(data.message || `Server returned ${res.status}`);
        pages = data.total_pages || 1;
        all.push(...(data.so_list || []));
        setExportProgress(`${p}/${pages}`);
      }

      if (all.length === 0) { flash('Nothing to export for these filters'); return; }

      // Cancellation columns exist only on the cancelled read, which comes from
      // so_headers_del — see acc-so-list.js's extraCols.
      const headers = [
        'SO Date', 'Voucher No', 'Buyer', 'Consignee', 'PO Number',
        'Qty', 'Actual Qty', 'Status', 'Advance Booked', 'Boxes', 'Weight (kg)',
        ...(cancelled ? ['Cancelled By', 'Cancellation Reason', 'Cancelled On'] : []),
      ];

      const dataRows = all.map((r) => [
        toDate(r.so_date),
        r.voucher_no,
        r.buyer || '',
        r.consignee || '',
        r.order_ref_no || '',
        // Through num() so these are real numbers with blanks as empty cells —
        // a single text cell in the column is enough to break SUM silently.
        num(r.total_quantity),
        num(r.total_actual_qty),
        // Same derivation the pills use, so the file can't disagree with the screen.
        SMAP[derivedStatus(r, cancelled)].label,
        r.booking_type === 'advance_booked' ? 'Yes' : 'No',
        num(r.boxes_packed),
        num(r.total_weight_kg),
        ...(cancelled ? [r.deleted_by || '', r.deletion_reason || '', toDate(r.deleted_at)] : []),
      ]);

      exportSheet({
        headers,
        rows: dataRows,
        sheetName: 'Sales Orders',
        filename: `Sales_Orders_${status}_${todayStamp()}.xlsx`,
      });
    } catch (err) {
      flash(err.message || 'Failed to build the Excel file');
    } finally {
      setExporting(false);
      setExportProgress('');
    }
  }

  const lineItems = detail?.line_items || [];
  const canCancel = !!header && !isCancelled;

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'linear-gradient(160deg, #FAF8FC 0%, #f2edf8 100%)', padding: 22, gap: 22, boxSizing: 'border-box', color: '#3b3340', fontFamily: 'Nunito, system-ui, sans-serif' }}>
      <main style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 20 }} data-tutorial="so-queue-table">
        <ListHero
          flush
          kicker="Invoicing"
          title="Sales Orders"
          hint="Click on any sales order to see its line items, or the PDF icon to open the SO PDF."
          icons={['📋', '🛒', '📑', '📦', '📋', '🧾']}
          onDownload={downloadExcel}
          downloading={exporting}
          downloadTitle={exporting ? `Preparing Excel… ${exportProgress}` : 'Download Excel — every sales order matching the current filters'}
        />

        <ListTopRow
          flush
          card={<CountCard from={from} to={to} count={total} loading={loading}
            label={status === 'all' ? 'Sales orders made' : `${SMAP[status].label} sales orders`} />}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, height: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#FAF8FC', padding: '6px 8px 6px 20px', borderRadius: 999, flex: '1 1 260px', boxShadow: inset }}>
                <span style={{ fontSize: 15, opacity: 0.5 }}>🔍</span>
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search voucher no, buyer, PO no..."
                  style={{ flex: 1, border: 0, outline: 'none', background: 'transparent', font: 'inherit', fontSize: 15, fontWeight: 600, color: '#3b3340', padding: '12px 0' }}
                />
              </div>
              <div>
                <DataFreshness
                  fetchedAt={list.fetchedAt}
                  loading={loading}
                  onRefresh={() => { clearCaches(); revalidate(); }}
                  style={{ marginTop: 0 }}
                />
              </div>
            </div>
            {/* FILTERS */}
            <section style={{ background: '#FAF8FC', borderRadius: 30, padding: '18px 20px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '14px 18px', boxShadow: clay }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#FAF8FC', padding: '8px 14px', borderRadius: 20, boxShadow: inset }}>
                <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1.2, color: '#9a8fa0' }}>FROM</span>
                <input type="date" value={from} onChange={(e) => changeFilters(() => { setMonth('all'); setYear('all'); setFrom(e.target.value); })} style={{ border: 0, outline: 'none', background: 'transparent', font: 'inherit', fontSize: 14, fontWeight: 700, color: '#3b3340', padding: '8px 0' }} />
                <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1.2, color: '#9a8fa0', marginLeft: 6 }}>TO</span>
                <input type="date" value={to} onChange={(e) => changeFilters(() => { setMonth('all'); setYear('all'); setTo(e.target.value); })} style={{ border: 0, outline: 'none', background: 'transparent', font: 'inherit', fontSize: 14, fontWeight: 700, color: '#3b3340', padding: '8px 0' }} />
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                <select value={month} onChange={(e) => changeFilters(() => { setMonth(e.target.value); applyMonthYear(e.target.value, year); })} style={{ border: 0, cursor: 'pointer', font: 'inherit', fontSize: 13, fontWeight: 800, color: '#453c4b', background: '#f1ecf7', padding: '12px 16px', borderRadius: 16, boxShadow: inset }}>
                  <option value="all">All Months</option>
                  {NAMES.map((label, i) => <option key={label} value={String(i + 1)}>{label}</option>)}
                </select>
                <select value={year} onChange={(e) => changeFilters(() => { setYear(e.target.value); applyMonthYear(month, e.target.value); })} style={{ border: 0, cursor: 'pointer', font: 'inherit', fontSize: 13, fontWeight: 800, color: '#453c4b', background: '#f1ecf7', padding: '12px 16px', borderRadius: 16, boxShadow: inset }}>
                  <option value="all">All Years</option>
                  {YEARS.map((y) => <option key={y} value={String(y)}>{y}</option>)}
                </select>
                <button type="button" onClick={clearTime} style={{ border: 0, cursor: 'pointer', font: 'inherit', fontSize: 12.5, fontWeight: 800, color: '#a33b33', background: '#f7dedb', padding: '11px 18px', borderRadius: 999, whiteSpace: 'nowrap', boxShadow: '6px 7px 14px rgba(180, 70, 60, 0.16), -5px -5px 12px rgba(255,255,255,0.95)' }}>
                  Clear time filter
                </button>
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
                {STATUSES.map((s) => {
                  const active = status === s.key;
                  const n = counts[s.key];
                  return (
                    <button
                      key={s.key}
                      type="button"
                      onClick={() => changeFilters(() => setStatus(s.key))}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 9, border: 0, cursor: 'pointer', font: 'inherit', fontSize: 12.5, fontWeight: 800,
                        padding: '11px 16px', borderRadius: 999, whiteSpace: 'nowrap',
                        color: active ? s.fg : '#6b6072', background: active ? s.bg : '#FAF8FC',
                        boxShadow: active ? `inset 4px 4px 9px ${s.ring}, inset -4px -4px 9px rgba(255,255,255,0.95)` : raised,
                      }}
                    >
                      <span style={{ width: 9, height: 9, borderRadius: 999, background: s.fg, opacity: active ? 1 : 0.55 }} />
                      {s.label}
                      <span style={{ fontSize: 11, fontWeight: 800, color: active ? s.fg : '#9a8fa0', opacity: 0.85 }}>
                        {n === undefined ? '' : n}
                      </span>
                    </button>
                  );
                })}
              </div>
            </section>

          </div>
        </ListTopRow>

        {error && (
          <div style={{ background: '#f7dedb', color: '#7d2a24', borderRadius: 18, padding: '14px 20px', fontSize: 14, fontWeight: 700, boxShadow: inset }}>
            Couldn't load sales orders: {error}
          </div>
        )}

        {/* TABLE */}
        <section style={{ position: 'relative', background: '#FAF8FC', borderRadius: 27, padding: 14, overflowX: 'auto', boxShadow: '14px 16px 34px rgba(104, 90, 160, 0.22), -12px -12px 28px rgba(255,255,255,0.9)' }}>
          {/* Visible "working" state. Previously the old rows just sat there
              unchanged during a fetch, which read as the page having frozen
              rather than loading. Rows stay in place (deliberately — no layout
              jump) but dim and stop accepting clicks. */}
          {loading && rows.length > 0 && (
            <div style={{ position: 'absolute', inset: 0, borderRadius: 27, background: 'rgba(250,248,252,0.55)', zIndex: 5, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: 26, pointerEvents: 'all' }}>
              <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: 1, color: '#6b6072', background: '#FAF8FC', padding: '9px 18px', borderRadius: 999, boxShadow: raised }}>Loading…</span>
            </div>
          )}
          <div style={{ minWidth: TABLE_MIN_WIDTH, padding: 4, opacity: loading && rows.length > 0 ? 0.45 : 1, transition: 'opacity .12s ease' }}>
            <div style={{ display: 'grid', gridTemplateColumns: GRID, gap: 13, alignItems: 'center', background: '#322a38', color: '#efe6f7', borderRadius: 19, padding: '14px 19px', fontSize: 10, fontWeight: 800, letterSpacing: 1.3, boxShadow: '6px 8px 18px rgba(50, 42, 56, 0.32), inset 0 2px 0 rgba(255,255,255,0.12)' }}>
              <div>VOUCHER</div>
              <div>BUYER</div>
              <div>PO</div>
              <div>SO DATE</div>
              <div style={{ textAlign: 'right' }}>QTY</div>
              <div style={{ textAlign: 'center' }}>PDF</div>
              <div style={{ textAlign: 'right' }}>STATUS</div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 11 }}>
              {rows.map((row) => {
                const st = derivedStatus(row, status === 'cancelled');
                return (
                  <div
                    key={row.id + '-' + row.voucher_no}
                    onClick={() => openDetail(row.voucher_no, status === 'cancelled')}
                    style={{ display: 'grid', gridTemplateColumns: GRID, gap: 13, alignItems: 'center', background: '#fbfafd', borderRadius: 19, padding: '13px 19px', cursor: 'pointer', boxShadow: '8px 9px 18px rgba(104, 90, 160, 0.18), -7px -7px 16px rgba(255,255,255,0.95)' }}
                  >
                    <div style={{ fontSize: 13.5, fontWeight: 800, color: '#2f2833' }}>{row.voucher_no}</div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#4a4150', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.buyer || row.consignee || '—'}</div>
                    <div style={{ fontSize: 12.5, fontWeight: 700, color: '#6d4fd6', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.order_ref_no || '—'}</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#6b6072', whiteSpace: 'nowrap' }}>{fmtDate(row.so_date)}</div>
                    <div style={{ fontSize: 13, fontWeight: 800, color: '#2f2833', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{Number(row.total_quantity || 0).toLocaleString('en-IN')}</div>
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      <PdfButton onClick={(e) => openRowPdf(e, row.voucher_no)} loading={rowPdfLoading === row.voucher_no} title={`Open SO ${row.voucher_no} PDF in a new tab`} />
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'flex-end' }}>
                      <span style={pillStyle(st, true)}>{SMAP[st].label}</span>
                      {row.booking_type === 'advance_booked' && <span style={bookedPillStyle()}>{BOOKED_PILL.label}</span>}
                    </div>
                  </div>
                );
              })}
              {!loading && rows.length === 0 && (
                <div style={{ padding: '40px 16px', textAlign: 'center', fontSize: 13, fontWeight: 700, color: '#8c8090' }}>
                  No sales orders match these filters.
                </div>
              )}
            </div>
          </div>

          {/* PAGINATION */}
          {!loading && rows.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14, marginTop: 16 }}>
              <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
                style={{ border: 0, font: 'inherit', fontSize: 11, fontWeight: 800, color: page <= 1 ? '#b5adbd' : '#453c4b', background: '#FAF8FC', padding: '10px 18px', borderRadius: 999, cursor: page <= 1 ? 'not-allowed' : 'pointer', boxShadow: page <= 1 ? 'none' : raised }}>
                ← Prev 50
              </button>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#8c8090' }}>Page {page} of {totalPages}</span>
              <button type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                style={{ border: 0, font: 'inherit', fontSize: 11, fontWeight: 800, color: page >= totalPages ? '#b5adbd' : '#453c4b', background: '#FAF8FC', padding: '10px 18px', borderRadius: 999, cursor: page >= totalPages ? 'not-allowed' : 'pointer', boxShadow: page >= totalPages ? 'none' : raised }}>
                Next 50 →
              </button>
            </div>
          )}
        </section>
      </main>

      {/* DETAIL MODAL */}
      {openRow && (
        <div onClick={closeDetail} style={{ position: 'fixed', inset: 0, background: 'rgba(58, 48, 58, 0.42)', backdropFilter: 'blur(3px)', display: 'grid', placeItems: 'center', padding: 40, zIndex: 40 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 900, maxHeight: '88vh', overflowY: 'auto', background: '#FAF8FC', borderRadius: 38, padding: '30px 34px 34px', boxShadow: '18px 22px 50px rgba(60, 45, 55, 0.35), -12px -12px 30px rgba(255,255,255,0.55)' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 20 }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 30, fontWeight: 800, color: '#2f2833' }}>SO {openRow}</h2>
                <p style={{ margin: '6px 0 0', fontSize: 15, fontWeight: 700, color: '#8c8090' }}>{header?.buyer || header?.consignee || (detailLoading ? 'Loading…' : '')}</p>
                {isCancelled && (
                  <div style={{ marginTop: 14, display: 'inline-flex', flexDirection: 'column', gap: 3, background: '#f7dedb', padding: '12px 18px', borderRadius: 18, boxShadow: 'inset 3px 3px 8px rgba(180, 70, 60, 0.22), inset -3px -3px 8px rgba(255,255,255,0.9)' }}>
                    <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1.2, color: '#a33b33' }}>CANCELLATION REASON</span>
                    <span style={{ fontSize: 14, fontWeight: 700, color: '#7d2a24' }}>{header?.deletion_reason || '—'}</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#a33b33', opacity: 0.8 }}>
                      by {header?.deleted_by || '—'} on {fmtDate(header?.deleted_at)}
                    </span>
                  </div>
                )}
              </div>
              <button type="button" onClick={closeDetail} style={{ border: 0, cursor: 'pointer', font: 'inherit', fontWeight: 800, fontSize: 14, color: '#453c4b', background: '#FAF8FC', padding: '14px 26px', borderRadius: 999, boxShadow: '7px 8px 16px rgba(104, 90, 160, 0.24), -6px -6px 14px rgba(255,255,255,0.95)' }}>Close</button>
            </div>

            {detail?.error && (
              <div style={{ marginTop: 22, background: '#f7dedb', color: '#7d2a24', borderRadius: 18, padding: '14px 20px', fontSize: 14, fontWeight: 700 }}>{detail.error}</div>
            )}

            {header && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 14, marginTop: 26 }}>
                  {[
                    { label: 'SO DATE',    value: fmtDate(header.so_date) },
                    { label: 'ORDER REF',  value: header.order_ref_no || '—' },
                    { label: 'TOTAL QTY',  value: Number(header.total_quantity || 0).toLocaleString('en-IN'), accent: true },
                    { label: 'STATE NAME', value: header.place_of_supply || '—' },
                  ].map((f) => (
                    <div key={f.label} style={{ background: '#FAF8FC', borderRadius: 22, padding: '16px 18px', boxShadow: inset }}>
                      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1.4, color: '#9a8fa0' }}>{f.label}</div>
                      <div style={{ marginTop: 7, fontSize: 20, fontWeight: 800, color: f.accent ? '#6d4fd6' : '#2f2833' }}>{f.value}</div>
                    </div>
                  ))}
                </div>

                <h3 style={{ margin: '30px 0 14px', fontSize: 13, fontWeight: 800, letterSpacing: 1.8, color: '#6b6072' }}>LINE ITEMS</h3>
                <div style={{ background: '#f5f1fa', borderRadius: 26, padding: 14, boxShadow: 'inset 5px 5px 12px rgba(104, 90, 160, 0.18), inset -5px -5px 12px rgba(255,255,255,0.9)' }}>
                  {(() => {
                    const cols = isBooked && !isCancelled
                      ? '60px minmax(0, 1fr) 150px 80px 110px 90px'
                      : '60px minmax(0, 1fr) 150px 80px 90px';
                    return (
                      <>
                        <div style={{ display: 'grid', gridTemplateColumns: cols, gap: 12, padding: '14px 20px', borderRadius: 18, background: '#322a38', color: '#efe6f7', fontSize: 11, fontWeight: 800, letterSpacing: 1.4 }}>
                          <div>SI NO</div>
                          <div>SKU</div>
                          <div>EAN</div>
                          <div style={{ textAlign: 'right' }}>MRP</div>
                          {isBooked && !isCancelled && <div style={{ textAlign: 'right' }}>ACTUAL QTY</div>}
                          <div style={{ textAlign: 'right' }}>SO QTY</div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
                          {lineItems.map((it, i) => (
                            <div key={it.id} style={{ display: 'grid', gridTemplateColumns: cols, gap: 12, alignItems: 'center', padding: '14px 20px', borderRadius: 18, background: '#fcfbfe', boxShadow: '5px 6px 12px rgba(104, 90, 160, 0.14), -4px -4px 10px rgba(255,255,255,0.95)' }}>
                              <div style={{ fontWeight: 800, color: '#8c8090' }}>{i + 1}</div>
                              <div style={{ fontSize: 14, fontWeight: 700, color: '#3b3340' }}>{it.sku_name}</div>
                              <div style={{ fontSize: 13, fontWeight: 600, color: '#6d4fd6', fontVariantNumeric: 'tabular-nums' }}>{it.ean || '—'}</div>
                              <div style={{ fontSize: 14, fontWeight: 700, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{it.mrp ?? '—'}</div>
                              {isBooked && !isCancelled && (
                                <div style={{ textAlign: 'right' }}>
                                  <input
                                    type="number"
                                    value={edited[it.id] !== undefined ? edited[it.id] : (it.actual_qty ?? '')}
                                    onChange={(e) => setEdited((p) => ({ ...p, [it.id]: e.target.value }))}
                                    style={{ width: 80, border: 0, outline: 'none', background: '#f1ecf7', font: 'inherit', fontSize: 14, fontWeight: 800, color: '#3b3340', textAlign: 'right', padding: '8px 10px', borderRadius: 12, boxShadow: inset }}
                                  />
                                </div>
                              )}
                              <div style={{ fontSize: 14, fontWeight: 800, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{it.quantity}</div>
                            </div>
                          ))}
                          {lineItems.length === 0 && !detailLoading && (
                            <div style={{ padding: '24px 20px', textAlign: 'center', fontSize: 14, fontWeight: 700, color: '#8c8090' }}>No line items recorded for this SO.</div>
                          )}
                        </div>
                      </>
                    );
                  })()}
                </div>

                <div style={{ display: 'flex', gap: 14, marginTop: 26, flexWrap: 'wrap' }}>
                  <button type="button" onClick={viewPdf} disabled={pdfLoading}
                    style={{ border: 0, cursor: pdfLoading ? 'default' : 'pointer', font: 'inherit', fontSize: 15, fontWeight: 800, color: '#1d6f7a', background: '#d9eef1', padding: '18px 30px', borderRadius: 999, opacity: pdfLoading ? 0.6 : 1, boxShadow: '8px 10px 20px rgba(40, 130, 145, 0.24), -6px -6px 14px rgba(255,255,255,0.9)' }}>
                    {pdfLoading ? 'Opening…' : 'View SO PDF'}
                  </button>

                  {canBook && (
                    <button type="button" onClick={toggleBooking} disabled={saving}
                      style={{ border: 0, cursor: saving ? 'default' : 'pointer', font: 'inherit', fontSize: 15, fontWeight: 800, color: '#4b2fb0', background: '#e7e0f8', padding: '18px 30px', borderRadius: 999, opacity: saving ? 0.6 : 1, boxShadow: '8px 10px 20px rgba(110, 90, 170, 0.28), -6px -6px 14px rgba(255,255,255,0.9)' }}>
                      {isBooked ? 'Unmark Advance Booking' : 'Mark as Advance Booked'}
                    </button>
                  )}

                  {isBooked && !isCancelled && Object.keys(edited).length > 0 && (
                    <button type="button" onClick={saveQuantities} disabled={saving}
                      style={{ border: 0, cursor: saving ? 'default' : 'pointer', font: 'inherit', fontSize: 15, fontWeight: 800, color: '#2f7a52', background: '#dcefe2', padding: '18px 30px', borderRadius: 999, opacity: saving ? 0.6 : 1, boxShadow: '8px 10px 20px rgba(60, 130, 90, 0.24), -6px -6px 14px rgba(255,255,255,0.9)' }}>
                      {saving ? 'Saving…' : 'Save Quantities'}
                    </button>
                  )}

                  {canCancel && (
                    <button type="button" onClick={() => { setCancelOpen(true); setCancelReason(''); setCancelAck(false); }}
                      style={{ border: 0, cursor: 'pointer', font: 'inherit', fontSize: 15, fontWeight: 800, color: '#a33b33', background: '#f7dedb', padding: '18px 30px', borderRadius: 999, boxShadow: '8px 10px 20px rgba(180, 70, 60, 0.24), -6px -6px 14px rgba(255,255,255,0.9)' }}>
                      Cancel SO
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* CANCEL CONFIRMATION — clay-styled to match this page. Enforces the
          same two gates as the shared CancelConfirmModal (non-empty reason +
          explicit acknowledgement); the server also rejects an empty reason. */}
      {cancelOpen && (
        <div onClick={() => setCancelOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(58, 48, 58, 0.5)', backdropFilter: 'blur(3px)', display: 'grid', placeItems: 'center', padding: 40, zIndex: 60 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 560, background: '#FAF8FC', borderRadius: 32, padding: '30px 32px', boxShadow: '20px 24px 54px rgba(60, 45, 55, 0.38), -12px -12px 30px rgba(255,255,255,0.55)' }}>
            <h2 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: '#2f2833' }}>Cancel this Sales Order?</h2>
            <p style={{ margin: '12px 0 0', fontSize: 14.5, fontWeight: 600, lineHeight: 1.5, color: '#6b6072' }}>
              Cancelling <strong style={{ color: '#2f2833' }}>SO {openRow}</strong> moves it out of the live tables permanently. This cannot be undone from the portal.
            </p>

            <label style={{ display: 'block', marginTop: 22, fontSize: 13, fontWeight: 800, color: '#453c4b' }}>
              Reason for cancellation <span style={{ color: '#a33b33' }}>*</span>
            </label>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Explain why this is being cancelled..."
              rows={4}
              style={{ marginTop: 10, width: '100%', boxSizing: 'border-box', border: 0, outline: 'none', resize: 'vertical', font: 'inherit', fontSize: 14, fontWeight: 600, color: '#3b3340', background: '#f1ecf7', padding: '14px 16px', borderRadius: 18, boxShadow: inset }}
            />

            <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 18, fontSize: 13.5, fontWeight: 700, color: '#453c4b', cursor: 'pointer' }}>
              <input type="checkbox" checked={cancelAck} onChange={(e) => setCancelAck(e.target.checked)} style={{ width: 18, height: 18, accentColor: '#a33b33', cursor: 'pointer' }} />
              I understand this action is permanent and cannot be undone.
            </label>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 26 }}>
              <button type="button" onClick={() => setCancelOpen(false)} disabled={cancelling} style={{ border: 0, cursor: 'pointer', font: 'inherit', fontSize: 14, fontWeight: 800, color: '#453c4b', background: '#FAF8FC', padding: '14px 24px', borderRadius: 999, boxShadow: raised }}>
                Back
              </button>
              <button
                type="button"
                disabled={!(cancelReason.trim() && cancelAck) || cancelling}
                onClick={confirmCancel}
                style={{
                  border: 0, font: 'inherit', fontSize: 14, fontWeight: 800,
                  color: cancelReason.trim() && cancelAck ? '#fff' : '#c79a95',
                  background: cancelReason.trim() && cancelAck ? '#c14b3f' : '#f3e2df',
                  padding: '14px 26px', borderRadius: 999,
                  cursor: cancelReason.trim() && cancelAck && !cancelling ? 'pointer' : 'not-allowed',
                  boxShadow: cancelReason.trim() && cancelAck ? '6px 7px 14px rgba(180, 70, 60, 0.32), -5px -5px 12px rgba(255,255,255,0.5)' : 'none',
                }}
              >
                {cancelling ? 'Cancelling…' : 'Confirm Cancellation'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div style={{ position: 'fixed', bottom: 28, right: 28, padding: '15px 24px', borderRadius: 20, background: '#9FC7A6', color: '#1F3527', fontSize: 15, fontWeight: 800, boxShadow: '9px 9px 20px rgba(90,130,100,0.34), -7px -7px 16px rgba(255,255,255,0.7)', zIndex: 80 }}>
          {toast}
        </div>
      )}
    </div>
  );
}
